import { createContext, useContext, useMemo } from 'react';
import type { Notification } from '@/types/notification';
import type { ProfileVisit } from '@/types/profile-visit';

export interface NotificationCountContextType {
  unreadCount: number;
  unseenVisitsCount: number;
}

export interface NotificationListContextType {
  notifications: Notification[];
  profileVisits: ProfileVisit[];
  isLoading: boolean;
}

export interface NotificationActionsContextType {
  loadNotifications: () => Promise<void>;
  markAsRead: (notificationIds: number[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: number) => Promise<void>;
  loadProfileVisits: () => Promise<void>;
  markVisitsAsSeen: (visitIds: number[]) => Promise<void>;
  requestPushPermission: () => Promise<void>;
  isPushEnabled: boolean;
}

type NotificationContextType = NotificationCountContextType &
  NotificationListContextType &
  NotificationActionsContextType;

export const NotificationCountContext = createContext<NotificationCountContextType | null>(null);
export const NotificationListContext = createContext<NotificationListContextType | null>(null);
export const NotificationActionsContext = createContext<NotificationActionsContextType | null>(null);

export function useNotificationCount(): NotificationCountContextType {
  const context = useContext(NotificationCountContext);
  if (!context) {
    throw new Error('useNotificationCount must be used within NotificationProvider');
  }
  return context;
}

export function useNotificationList(): NotificationListContextType {
  const context = useContext(NotificationListContext);
  if (!context) {
    throw new Error('useNotificationList must be used within NotificationProvider');
  }
  return context;
}

export function useNotificationActions(): NotificationActionsContextType {
  const context = useContext(NotificationActionsContext);
  if (!context) {
    throw new Error('useNotificationActions must be used within NotificationProvider');
  }
  return context;
}

export function useNotifications(): NotificationContextType {
  const count = useNotificationCount();
  const list = useNotificationList();
  const actions = useNotificationActions();
  return useMemo(() => ({ ...count, ...list, ...actions }), [count, list, actions]);
}
